import { useState } from "react";
import { useLocation } from "react-router-dom";
import { UserPlus, List, Users as UsersIcon, X, Eye } from "lucide-react";
import DataTable from "../components/DataTable";

const clientsData = [
  {
    id: "client-1", name: "Acme Corporation",
    panGstinList: [
      { pan: "ABCDE1234F", gstins: ["29ABCDE1234F1Z5", "27ABCDE1234F2Z5"],
        users: [
          { id: "u1", name: "John Smith",     email: "john.smith@acme.com",   phone: "+1 555 0123", userId: "USR001", lastLogin: "2026-03-06 09:30", role: "Admin" },
          { id: "u2", name: "Jane Doe",       email: "jane.doe@acme.com",     phone: "+1 555 0124", userId: "USR002", lastLogin: "2026-03-05 14:20", role: "User" },
        ]},
      { pan: "ABCDE5678G", gstins: ["24ABCDE5678G1Z3"],
        users: [
          { id: "u3", name: "Robert Johnson", email: "robert.j@acme.com",     phone: "+1 555 0130", userId: "USR010", lastLogin: "2026-03-05 11:45", role: "User" },
          { id: "u4", name: "Lisa Anderson",  email: "lisa.a@acme.com",       phone: "+1 555 0131", userId: "USR011", lastLogin: "2026-03-04 16:20", role: "User" },
        ]},
    ], transactions: 1248, pending: 68,
  },
  {
    id: "client-2", name: "Global Tech Solutions",
    panGstinList: [
      { pan: "FGHIJ5678K", gstins: ["27FGHIJ5678K2Y4", "29FGHIJ5678K1Y5"],
        users: [
          { id: "u5", name: "Sarah Johnson",  email: "sarah.j@globaltech.com", phone: "+1 555 0125", userId: "USR003", lastLogin: "2026-03-06 08:15", role: "Admin" },
          { id: "u6", name: "Mark Wilson",    email: "mark.w@globaltech.com",  phone: "+1 555 0132", userId: "USR012", lastLogin: "2026-03-06 07:30", role: "User" },
        ]},
    ], transactions: 856, pending: 42,
  },
  {
    id: "client-3", name: "Enterprise Dynamics",
    panGstinList: [
      { pan: "KLMNO9012L", gstins: ["22KLMNO9012L1Z7"],
        users: [
          { id: "u7", name: "Michael Brown",  email: "m.brown@enterprise.com", phone: "+1 555 0126", userId: "USR004", lastLogin: "2026-03-05 16:45", role: "Admin" },
        ]},
    ], transactions: 642, pending: 31,
  },
];

const bcgUsers = [
  { id: "u1", name: "Riya Sharma",    email: "riya.sharma@bcg.com",    phone: "+91 98765 43210", userId: "BCG001", lastLogin: "2026-03-06 09:30", role: "Admin" },
  { id: "u2", name: "Arjun Mehta",    email: "arjun.mehta@bcg.com",    phone: "+91 98765 43211", userId: "BCG002", lastLogin: "2026-03-05 14:20", role: "User" },
  { id: "u3", name: "Priya Nair",     email: "priya.nair@bcg.com",     phone: "+91 98765 43212", userId: "BCG003", lastLogin: "2026-03-04 11:30", role: "User" },
];

export default function UsersPage() {
  const location    = useLocation();
  const isClient    = location.pathname.startsWith("/client");
  const [viewMode, setViewMode]     = useState(isClient ? "all" : "grouped");
  const [selected, setSelected]     = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm]             = useState({ name: "", email: "", role: "User", phone: "" });

  const allUsers = clientsData.flatMap(c => c.panGstinList.flatMap(pg => pg.users.map(u => ({ ...u, corporate: c.name, pan: pg.pan }))));

  const allCols = [
    { key: "name",      label: "Name",          sortable: true },
    { key: "corporate", label: "Corporate",     sortable: true },
    { key: "pan",       label: "PAN",           render: (v) => <span className="font-mono text-sm">{v}</span> },
    { key: "email",     label: "Email" },
    { key: "phone",     label: "Phone" },
    { key: "userId",    label: "User ID",       render: (v) => <span className="font-mono text-sm">{v}</span> },
    { key: "lastLogin", label: "Last Login",    sortable: true },
    { key: "role",      label: "Role",          render: (v) => <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${v === "Admin" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"}`}>{v}</span> },
  ];

  const clientCols = [
    { key: "name",      label: "Name",       sortable: true },
    { key: "email",     label: "Email" },
    { key: "phone",     label: "Phone" },
    { key: "userId",    label: "User ID",    render: (v) => <span className="font-mono text-sm">{v}</span> },
    { key: "lastLogin", label: "Last Login", sortable: true },
    { key: "role",      label: "Role",       render: (v) => <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${v === "Admin" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"}`}>{v}</span> },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">Users & Directory</h1>
          <p className="text-sm text-gray-500 mt-1">{isClient ? "BCG India user directory" : "Manage user access across all clients"}</p>
        </div>
        <div className="flex items-center gap-3">
          {!isClient && (
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button onClick={() => setViewMode("grouped")} className={`flex items-center gap-2 px-3 py-2 text-sm ${viewMode === "grouped" ? "bg-[#367588] text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}>
                <UsersIcon className="w-4 h-4" /> Grouped
              </button>
              <button onClick={() => setViewMode("all")} className={`flex items-center gap-2 px-3 py-2 text-sm ${viewMode === "all" ? "bg-[#367588] text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}>
                <List className="w-4 h-4" /> All Users
              </button>
            </div>
          )}
          <button onClick={() => setShowInvite(true)} className="flex items-center gap-2 px-4 py-2 bg-[#367588] text-white rounded-lg hover:bg-[#2a5a6b] transition-colors text-sm font-medium">
            <UserPlus className="w-4 h-4" /> {isClient ? "Invite User" : "Add User"}
          </button>
        </div>
      </div>

      {/* Client portal — flat list */}
      {isClient && <DataTable columns={clientCols} data={bcgUsers} />}

      {/* Admin — grouped view */}
      {!isClient && viewMode === "grouped" && (
        <div className="space-y-4">
          {clientsData.map(c => (
            <div key={c.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between p-5 cursor-pointer hover:bg-gray-50" onClick={() => setSelected(selected?.id === c.id ? null : c)}>
                <div>
                  <h3 className="font-semibold text-gray-900">{c.name}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{c.panGstinList.length} PAN(s) · {c.panGstinList.flatMap(pg => pg.users).length} users · {c.transactions.toLocaleString()} transactions</p>
                </div>
                <button className="text-sm text-[#367588] hover:underline flex items-center gap-1"><Eye className="w-4 h-4" /> View</button>
              </div>
              {selected?.id === c.id && (
                <div className="border-t border-gray-200 p-5 space-y-6">
                  {c.panGstinList.map(pg => (
                    <div key={pg.pan}>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-sm font-semibold text-gray-700">PAN: <span className="font-mono">{pg.pan}</span></span>
                        <div className="flex gap-2 flex-wrap">
                          {pg.gstins.map(g => <span key={g} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-mono">{g}</span>)}
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50"><tr>{["Name","Email","Phone","User ID","Last Login","Role"].map(h => <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>)}</tr></thead>
                          <tbody className="divide-y divide-gray-100">
                            {pg.users.map(u => (
                              <tr key={u.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 font-medium text-gray-900">{u.name}</td>
                                <td className="px-4 py-2 text-gray-600">{u.email}</td>
                                <td className="px-4 py-2 text-gray-600">{u.phone}</td>
                                <td className="px-4 py-2 font-mono text-gray-600">{u.userId}</td>
                                <td className="px-4 py-2 text-gray-600">{u.lastLogin}</td>
                                <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.role === "Admin" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"}`}>{u.role}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Admin — all users flat */}
      {!isClient && viewMode === "all" && <DataTable columns={allCols} data={allUsers} />}

      {/* Invite modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowInvite(false)}>
          <div className="bg-white rounded-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Invite User</h3>
              <button onClick={() => setShowInvite(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              {[["name","Full Name","text","Enter full name"],["email","Email Address","email","Enter email address"],["phone","Phone Number","tel","Enter phone number"]].map(([key, label, type, ph]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input type={type} value={form[key]} onChange={e => setForm({...form, [key]: e.target.value})} placeholder={ph}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#367588] focus:border-transparent outline-none text-sm" />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#367588] focus:border-transparent outline-none text-sm">
                  <option>User</option><option>Admin</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => { alert(`Invite sent to ${form.email}`); setShowInvite(false); }} className="flex-1 bg-[#367588] text-white py-2.5 rounded-lg font-semibold hover:bg-[#2a5a6b] transition-colors text-sm">Send Invite</button>
                <button onClick={() => setShowInvite(false)} className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
